import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that shows a notification when the cart total is below the minimum order amount.
 *
 * @extends {Component}
 */
class CartMinimumOrderNotification extends Component {
  minimumOrder = 50000; // £500 in cents

  connectedCallback() {
    super.connectedCallback();
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);

    // Get minimum order from data attribute
    const minimumOrderAttr = this.dataset.minimumOrder;
    if (minimumOrderAttr) {
      this.minimumOrder = parseInt(minimumOrderAttr) || 50000;
    }

    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    // Prevent form submission if below minimum order
    const cartForm = document.getElementById('cart-form');
    if (cartForm) {
      cartForm.addEventListener('submit', this.handleFormSubmit);
    }

    // Initialize display from data attribute or fetch cart
    const initialTotal = parseInt(this.dataset.cartTotal || '0') || 0;
    if (initialTotal > 0) {
      this.updateDisplay(initialTotal);
    } else {
      this.fetchCartTotal();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    
    // Remove form submit listener
    const cartForm = document.getElementById('cart-form');
    if (cartForm) {
      cartForm.removeEventListener('submit', this.handleFormSubmit);
    }
  }

  /**
   * Fetches the current cart total from the cart API
   */
  async fetchCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateDisplay(cart.total_price || 0);
    } catch (error) {
      console.error('Failed to fetch cart total:', error);
    }
  }

  /**
   * Handles cart update events
   * @param {CustomEvent} event - The cart update event
   */
  handleCartUpdate = async (event) => {
    // After cart updates, fetch the latest cart total
    // Small delay to ensure cart API is updated
    setTimeout(() => {
      this.fetchCartTotal();
    }, 100);
  };

  /**
   * Handles form submission to prevent checkout if below minimum order
   * @param {SubmitEvent} event - The form submit event
   */
  handleFormSubmit = async (event) => {
    // Only prevent if this is a checkout submission
    const submitter = event.submitter;
    const isCheckoutSubmission = submitter && (
      submitter.name === 'checkout' || 
      submitter.id === 'checkout' ||
      submitter.closest('[data-minimum-order-checkout]')
    );
    
    if (isCheckoutSubmission) {
      // Get current cart total - fetch fresh if needed
      let currentTotal = parseInt(this.dataset.cartTotal || '0') || 0;
      
      // If we don't have a current total, fetch it
      if (currentTotal === 0) {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          currentTotal = cart.total_price || 0;
          this.dataset.cartTotal = currentTotal.toString();
        } catch (error) {
          console.error('Failed to fetch cart total for validation:', error);
        }
      }
      
      // If below minimum, prevent submission
      if (currentTotal < this.minimumOrder) {
        event.preventDefault();
        event.stopPropagation();
        
        // Ensure notification is visible
        this.style.display = 'block';
        
        // Focus on the notification for accessibility
        this.setAttribute('tabindex', '-1');
        this.focus();
        
        // Scroll notification into view if needed
        this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        
        return false;
      }
    }
  };

  /**
   * Updates the display based on cart total
   * @param {number} cartTotal - The cart total in cents
   */
  updateDisplay(cartTotal) {
    const isBelowMinimum = cartTotal < this.minimumOrder;
    
    if (isBelowMinimum && cartTotal > 0) {
      this.style.display = 'block';
    } else {
      this.style.display = 'none';
    }

    // Update data attribute for reference
    this.dataset.cartTotal = cartTotal.toString();

    // Disable/enable checkout button
    this.updateCheckoutButton(isBelowMinimum);
  }

  /**
   * Updates the checkout button state based on minimum order requirement
   * @param {boolean} isBelowMinimum - Whether the cart is below the minimum order
   */
  updateCheckoutButton(isBelowMinimum) {
    // Disable/enable main checkout button
    const checkoutButton = document.querySelector('[data-minimum-order-checkout]');
    if (checkoutButton) {
      if (isBelowMinimum) {
        checkoutButton.disabled = true;
        checkoutButton.setAttribute('aria-disabled', 'true');
      } else {
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('aria-disabled');
      }
    }

    // Disable/enable additional checkout buttons (Shop Pay, etc.)
    const additionalCheckoutButtons = document.querySelector('[data-minimum-order-checkout-buttons]');
    if (additionalCheckoutButtons) {
      if (isBelowMinimum) {
        additionalCheckoutButtons.style.pointerEvents = 'none';
        additionalCheckoutButtons.style.opacity = '0.5';
        // Disable all buttons inside
        const buttons = additionalCheckoutButtons.querySelectorAll('button, a, [role="button"]');
        buttons.forEach(button => {
          button.setAttribute('aria-disabled', 'true');
          button.style.pointerEvents = 'none';
        });
      } else {
        additionalCheckoutButtons.style.pointerEvents = '';
        additionalCheckoutButtons.style.opacity = '';
        // Re-enable all buttons inside
        const buttons = additionalCheckoutButtons.querySelectorAll('button, a, [role="button"]');
        buttons.forEach(button => {
          button.removeAttribute('aria-disabled');
          button.style.pointerEvents = '';
        });
      }
    }
  }
}

if (!customElements.get('cart-minimum-order-notification')) {
  customElements.define('cart-minimum-order-notification', CartMinimumOrderNotification);
}

